import React, { useState, useCallback } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PaymentIcon from '@mui/icons-material/Payment';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid';

import PaginatedDataGrid, {
  PaginatedDataGridColumn,
  PaginatedDataGridAction
} from 'src/components/paginated-data-grid/PaginatedDataGrid';

import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import PaymentTypeDialog from './PaymentTypeDialog';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface PaymentType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

const PaymentTypesListing: React.FC = () => {
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState<PaymentType | null>(null);
  const [toggleLoading, setToggleLoading] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // ---------- Delete Payment Type ----------
  const handleDeleteClick = (id: GridRowId) => {
    setDeleteId(String(id));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;

    try {
      const response = await DataService.delete(`api/payment-types/${deleteId}`, {});
      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: 'Payment type deleted successfully!',
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to delete payment type');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to delete payment type. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
  };

  // ---------- Edit Payment Type ----------
  const handleEditClick = (paymentType: PaymentType) => {
    setSelectedPaymentType(paymentType);
    setEditDialogOpen(true);
  };

  // ---------- Add Payment Type ----------
  const handleAddPaymentType = () => {
    setSelectedPaymentType(null);
    setEditDialogOpen(true);
  };

  // ---------- Toggle Active/Inactive ----------
  const handleToggleActive = async (paymentType: PaymentType) => {
    setToggleLoading(paymentType.id);

    try {
      const endpoint = paymentType.active
        ? `api/payment-types/deactivate?id=${paymentType.id}`
        : `api/payment-types/activate?id=${paymentType.id}`;

      const response = await DataService.patch(endpoint, {});

      if (response.ok) {
        setRefreshKey((prev) => prev + 1);
        dispatch(
          showAlert({
            message: `Payment type ${paymentType.active ? 'deactivated' : 'activated'} successfully!`,
            type: 'success'
          })
        );
      } else {
        throw new Error('Failed to update payment type status');
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: 'Failed to update payment type status. Please try again.',
          type: 'error'
        })
      );
    } finally {
      setToggleLoading(null);
    }
  };

  // ---------- Handle Dialog Success ----------
  const handleDialogSuccess = () => {
    setEditDialogOpen(false);
    setRefreshKey((prev) => prev + 1);
    dispatch(
      showAlert({
        message: selectedPaymentType ? 'Payment type updated successfully!' : 'Payment type created successfully!',
        type: 'success'
      })
    );
  };

  // ---------- Data Grid Columns ----------
  const columns: PaginatedDataGridColumn[] = [
    {
      field: 'name',
      headerName: 'Payment Type',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.value || (
            <span style={{ fontStyle: 'italic' }}>
              No description provided
            </span>
          )}
        </Typography>
      )
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={params.row.active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleActive(params.row)}
              disabled={toggleLoading === params.row.id}
              color={params.row.active ? 'success' : 'default'}
            >
              {params.row.active ? <ToggleOnIcon /> : <ToggleOffIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.row)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row.id)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // ---------- Data Grid Actions (Toolbar) ----------
  const actions: PaginatedDataGridAction[] = [
    {
      key: 'add',
      title: 'Add Payment Type',
      icon: <AddIcon />,
      onClick: handleAddPaymentType,
      disabled: () => false,
      color: 'primary'
    }
  ];

  // ---------- Fetch Payment Types Function ----------
  const fetchPaymentTypes = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sortModel?: any;
      filterModel?: any;
      search?: string;
    }) => {
      console.log('fetchPaymentTypes called with params:', params);
      try {
        const response = await DataService.get('api/payment-types/all');

        if (response.ok) {
          const responseData = await response.json();
          console.log('API Response:', responseData);

          let rawPaymentTypes: any[] = [];

          if (responseData.success && responseData.data && Array.isArray(responseData.data)) {
            rawPaymentTypes = responseData.data;
          } else if (Array.isArray(responseData)) {
            rawPaymentTypes = responseData;
          } else {
            console.error('Unexpected response structure:', responseData);
            throw new Error('Invalid response structure');
          }

          const paymentTypes: PaymentType[] = rawPaymentTypes.map(paymentType => ({
            id: paymentType.id,
            name: paymentType.name,
            description: paymentType.description,
            active: paymentType.active
          }));

          console.log('Raw payment types from API:', rawPaymentTypes);
          console.log('Mapped payment types:', paymentTypes);

          // Filter by search term if provided
          let filteredPaymentTypes = paymentTypes;
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredPaymentTypes = paymentTypes.filter(
              paymentType =>
                paymentType.name.toLowerCase().includes(searchLower) ||
                (paymentType.description && paymentType.description.toLowerCase().includes(searchLower))
            );
          }

          // Apply sorting if provided
          if (params.sortModel && params.sortModel.length > 0) {
            const sortModel = params.sortModel[0];
            filteredPaymentTypes.sort((a, b) => {
              let aValue: any = a[sortModel.field as keyof PaymentType];
              let bValue: any = b[sortModel.field as keyof PaymentType];

              // Handle string comparison
              if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = (bValue as string).toLowerCase();
              }

              if (sortModel.sort === 'desc') {
                return aValue < bValue ? 1 : -1;
              }
              return aValue > bValue ? 1 : -1;
            });
          }

          // Apply pagination (DataGrid uses 0-based page indexing)
          const startIndex = params.page * params.pageSize;
          const paginatedPaymentTypes = filteredPaymentTypes.slice(startIndex, startIndex + params.pageSize);

          const totalPages = Math.ceil(filteredPaymentTypes.length / params.pageSize);

          const result = {
            items: paginatedPaymentTypes,
            totalCount: filteredPaymentTypes.length,
            pageNumber: params.page + 1,
            pageSize: params.pageSize,
            totalPages: totalPages,
            hasNext: params.page + 1 < totalPages,
            hasPrevious: params.page > 0
          };

          console.log('Final result being returned:', result);
          return result;
        } else {
          throw new Error(`Failed to fetch payment types: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Error fetching payment types:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to load payment types. Please try again.';
        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
        return {
          items: [],
          totalCount: 0,
          pageNumber: params.page + 1,
          pageSize: params.pageSize,
          totalPages: 0,
          hasNext: false,
          hasPrevious: false
        };
      }
    },
    [dispatch]
  );

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1.5, fontWeight: 'bold' }}>
        Payment Types Management
      </Typography>

      <PaginatedDataGrid
        key={refreshKey}
        title="Payment Types"
        columns={columns}
        useBackendPagination
        onDataFetch={fetchPaymentTypes}
        actions={actions}
        emptyMessage="No payment types found"
      />

      {/* Delete Confirmation Dialog */}
      <SimpleConfirmationDialog
        open={!!deleteId}
        title="Delete Payment Type"
        message="Are you sure you want to delete this payment type? This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />

      {/* Add/Edit Payment Type Dialog */}
      <PaymentTypeDialog
        open={editDialogOpen}
        paymentType={selectedPaymentType}
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleDialogSuccess}
      />
    </Box>
  );
};

export default PaymentTypesListing;